import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Alert, Steps, Button, Card, Checkbox, Input, Modal, Space, Typography, Empty, message } from "antd";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { api, errorMessage } from "../helpers/api";
import type { ApprovalTimelineResponse } from "../api-models";
import { DT_FORMATS } from "../helpers/constants";

const STATUS_TO_STEP: Record<string, "wait" | "process" | "finish" | "error"> = {
  WAITING: "wait",
  PENDING: "process",
  APPROVED: "finish",
  REJECTED: "error",
  SKIPPED: "finish",
};

// ApprovalLine is one line the approver can decline while approving the rest of the
// document (backend: LineRejectable). Only the document types that support it pass any.
export interface ApprovalLine {
  id: number;
  label: ReactNode;
  detail?: ReactNode;
}

// Renders on every document page: steps + actions + comments (architecture.md §8, §11).
// The approve/reject buttons appear when the API reports canAct — the same eligibility
// the /approvals inbox uses — so an approver can act from the document itself.
// onActed lets the host page refresh the document whose status the action just changed.
// refreshKey is the other direction: the approval instance is created server-side by the
// host's own actions (submit for approval above all), so the host passes its document
// status here to force a refetch — otherwise the card stays absent until a remount.
//
// lines turns the approve dialog into a line-by-line one: every line starts checked, so
// the default action is still "approve the document as submitted", and the ones the
// approver unchecks are posted as rejectedLineIds for the backend to decline. Leave it
// undefined for document types that are approved whole.
export function ApprovalTimeline({
  docType,
  docId,
  refreshKey,
  lines,
  onActed,
}: {
  docType: string;
  docId: number;
  refreshKey?: string | number;
  lines?: ApprovalLine[];
  onActed?: () => void;
}) {
  const [data, setData] = useState<ApprovalTimelineResponse | null>(null);
  const [approve, setApprove] = useState(true);
  const [actioning, setActioning] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [comment, setComment] = useState("");
  const [keptLineIds, setKeptLineIds] = useState<number[]>([]);

  const load = useCallback(async () => {
    const res = await api.get<ApprovalTimelineResponse>(`/approvals/${docType}/${docId}`);
    return res.data;
  }, [docType, docId]);

  useEffect(() => {
    let cancelled = false;
    load().then((timeline) => {
      if (!cancelled) setData(timeline);
    });
    return () => {
      cancelled = true;
    };
  }, [load, refreshKey]);

  function openAction(isApprove: boolean) {
    setApprove(isApprove);
    setComment("");
    // Everything checked: approving without touching the list approves the whole document.
    setKeptLineIds((lines ?? []).map((l) => l.id));
    setActioning(true);
  }

  const rejectedLineIds = (lines ?? []).filter((l) => !keptLineIds.includes(l.id)).map((l) => l.id);
  const nothingKept = !!lines && lines.length > 0 && keptLineIds.length === 0;

  async function submitAction() {
    setSubmitting(true);
    try {
      await api.post(`/approvals/${docType}/${docId}/${approve ? "approve" : "reject"}`, {
        comment,
        // Only ever sent on an approve: a rejection already declines every line.
        rejectedLineIds: approve ? rejectedLineIds : [],
      });
      message.success(
        approve
          ? rejectedLineIds.length > 0
            ? `Approved, with ${rejectedLineIds.length} line(s) not approved.`
            : "Approved."
          : "Rejected.",
      );
      setActioning(false);
      setData(await load());
      onActed?.();
    } catch (err) {
      message.error(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (!data || !data.instance) {
    return null;
  }

  return (
    <Card
      title="Approval"
      size="small"
      style={{ marginTop: 16 }}
      data-tour="approval-timeline"
      extra={
        data.canAct && (
          <Space>
            <Button icon={<CheckOutlined />} type="primary" onClick={() => openAction(true)} data-tour="approval-approve">
              Approve
            </Button>
            <Button icon={<CloseOutlined />} danger onClick={() => openAction(false)} data-tour="approval-reject">
              Reject
            </Button>
          </Space>
        )
      }
    >
      {data.steps.length === 0 ? (
        <Empty description="No approval steps" />
      ) : (
        <Steps
          direction="vertical"
          size="small"
          items={data.steps.map((s) => ({
            title: (s.roleName || `Step ${s.stepOrder}`) + (s.status === "SKIPPED" ? " (skipped)" : ""),
            status: STATUS_TO_STEP[s.status] ?? "wait",
            description: (
              <div>
                <Typography.Text type="secondary">{s.scopeRule}</Typography.Text>
                {s.action && (
                  <div>
                    <Typography.Text>
                      {s.action.action === "APPROVED" ? "Approved" : "Rejected"} by{" "}
                      {s.action.actor?.name ?? s.action.actor?.email ?? `user #${s.action.actedBy}`} on{" "}
                      {dayjs(s.action.actedAt).format(DT_FORMATS.dateTime)}
                    </Typography.Text>
                    {s.action.comment && <div>"{s.action.comment}"</div>}
                  </div>
                )}
              </div>
            ),
          }))}
        />
      )}
      <Modal
        title={approve ? "Approve" : "Reject"}
        open={actioning}
        onCancel={() => setActioning(false)}
        onOk={submitAction}
        okButtonProps={{ danger: !approve, loading: submitting, disabled: nothingKept }}
        destroyOnHidden
      >
        {approve && lines && lines.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <Typography.Text strong>Lines to approve</Typography.Text>
            <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
              Everything is approved unless you uncheck it. An unchecked line is not approved: it stays on the document,
              marked as declined, and nothing is issued or ordered against it.
            </Typography.Paragraph>
            <Checkbox.Group
              value={keptLineIds}
              onChange={(v) => setKeptLineIds(v as number[])}
              style={{ display: "flex", flexDirection: "column", gap: 6 }}
            >
              {lines.map((l) => (
                <Checkbox key={l.id} value={l.id}>
                  {l.label}
                  {l.detail && (
                    <Typography.Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                      {l.detail}
                    </Typography.Text>
                  )}
                </Checkbox>
              ))}
            </Checkbox.Group>
            {nothingKept && (
              <Alert
                type="warning"
                showIcon
                style={{ marginTop: 8 }}
                message="Nothing left to approve"
                description="Approving with every line unchecked would authorise nothing. Reject the document instead."
              />
            )}
            {rejectedLineIds.length > 0 && !nothingKept && (
              <Alert
                type="info"
                showIcon
                style={{ marginTop: 8 }}
                message={`${rejectedLineIds.length} line(s) will not be approved`}
                description="Your comment below is recorded against them as the reason."
              />
            )}
          </div>
        )}
        <Input.TextArea rows={3} placeholder="Comment (optional)" value={comment} onChange={(e) => setComment(e.target.value)} />
      </Modal>
    </Card>
  );
}
